import { zodResolver } from "@hookform/resolvers/zod";
import { Close, EmailOutlined } from "@mui/icons-material";
import {
  Avatar as MuiAvatar,
  Box,
  FormLabel,
  Input,
  Stack,
  Typography,
  Button,
  IconButton,
  FormHelperText,
} from "@mui/joy";
import { useNavigate } from "@tanstack/react-router";
import { HTTPError } from "griptonite-api-client/client";
import { ApiInviteStaffPayload } from "griptonite-api-client/types/accounts";
import { useMemo, useState } from "react";
import { FormProvider, useController, useForm, useFormContext } from "react-hook-form";
import { useDebounce } from "use-debounce";
import { z } from "zod";

import { useInviteStaffMutation, useProfilesSearchQuery } from "@/hooks/profile";
import { useAllVenueStaffData } from "@/hooks/venues";
import getThumbnailUrl from "@/utils/getThumbnailUrl";
import { transformValidationErrorToErrorsArray } from "@/utils/handleFormError";

import Avatar from "../Avatar";
import FormGroup from "../forms/FormGroup";
import TextInput from "../forms/TextInput";
import { StackLink } from "../Links";
import { FormSection } from "../RouteForm/RouteFormFields";
import { SidebarHeader } from "../Sidebar";
import { StaffRoleUi, useSelectedVenues } from "./Staff";

const emptyArray: never[] = [];

export default function AddStaffSidebar() {
  const [searchQ, setSearchQ] = useState("");
  const [debouncedSearchQ] = useDebounce(searchQ, 300);

  const profilesQuery = useProfilesSearchQuery(debouncedSearchQ);

  const navigate = useNavigate();

  const [showForm, setShowForm] = useState(false);

  const email = useMemo(() => {
    if (searchQ.includes("@")) {
      if (!profilesQuery.data?.data.some((profile) => profile.email === searchQ)) {
        return searchQ;
      }
    }
    return null;
  }, [searchQ, profilesQuery.data]);

  return (
    <Stack sx={{ height: "100dvh", width: 320, padding: 0 }}>
      <SidebarHeader>
        <Typography level="title-md">Add staff</Typography>
        <IconButton
          size="sm"
          onClick={() =>
            navigate({
              to: "..",
              from: "/gym/$venueUuid/staff/$userUuid",
              replace: true,
            })
          }
        >
          <Close />
        </IconButton>
      </SidebarHeader>
      <Stack
        sx={{
          flex: 1,
          overflowY: "auto",
        }}
      >
        {showForm ? (
          <InviteForm email={email ?? ""} />
        ) : (
          <Stack spacing={2} padding={2}>
            <Stack spacing={0.5}>
              <FormLabel>Name or email</FormLabel>
              <Input
                placeholder="Type to search for a user"
                onChange={(e) => setSearchQ(e.target.value)}
              />
            </Stack>
            {email || profilesQuery.data?.data.length ? (
              <Stack
                spacing={1}
                sx={{
                  border: "1px solid",
                  borderColor: "neutral.200",
                  borderRadius: 8,
                }}
              >
                {email ? (
                  <Box
                    onClick={() => {
                      setShowForm(true);
                    }}
                    padding={1}
                    sx={{
                      "&:hover": {
                        cursor: "pointer",
                        backgroundColor: "neutral.100",
                      },
                    }}
                  >
                    <Typography
                      level="body-sm"
                      startDecorator={<EmailOutlined />}
                      sx={{
                        alignItems: "flex-start",
                      }}
                    >
                      <Typography>
                        Invite{" "}
                        <Typography fontWeight="bold" sx={{ wordBreak: "break-all" }}>
                          {email}
                        </Typography>{" "}
                        by email
                      </Typography>
                    </Typography>
                  </Box>
                ) : null}
                {profilesQuery.data?.data.map((profile) => (
                  <StackLink
                    key={profile.uuid}
                    direction="row"
                    gap={1}
                    alignItems="center"
                    to="../$userUuid"
                    from="/gym/$venueUuid/staff/add"
                    params={{ userUuid: profile.uuid }}
                    sx={{
                      textDecoration: "none",
                      color: "inherit",
                      padding: 1,
                      borderTop: "1px solid",
                      borderColor: "neutral.200",
                      margin: "0!important",
                      "&:first-child": {
                        borderTop: "none",
                      },
                      "&:hover": {
                        backgroundColor: "neutral.100",
                      },
                    }}
                  >
                    <Box>
                      <Avatar user={profile} size={40} />
                    </Box>
                    <Stack>
                      <Typography level="title-sm">{profile.name}</Typography>
                      <Typography level="body-xs" color="neutral">
                        {profile.email}
                      </Typography>
                    </Stack>
                  </StackLink>
                ))}
              </Stack>
            ) : null}
          </Stack>
        )}
      </Stack>
    </Stack>
  );
}

const InviteStaffSchema = z.object({
  email: z.string().email(),
  firstName: z.string({ message: "First name is required" }).min(1, {
    message: "First name is required",
  }),
  lastName: z.string({ message: "Last name is required" }).min(1, {
    message: "Last name is required",
  }),
  roles: z
    .array(
      z.object({
        venue: z.string().uuid(),
        role: z.enum(["ADMIN", "STAFF", "ROUTE_SETTER"]),
      }),
      {
        message: "At least one role is required",
      },
    )
    .min(1, { message: "At least one role is required" }),
});

const InviteForm = ({ email }: { email: string }) => {
  const methods = useForm<ApiInviteStaffPayload>({
    defaultValues: { email },
    resolver: zodResolver(InviteStaffSchema),
    mode: "onSubmit",
  });

  console.log("ERR", methods.formState.errors);

  const mutation = useInviteStaffMutation();
  const navigate = useNavigate();

  const onSubmit = async (values: ApiInviteStaffPayload) => {
    await mutation.mutateAsync(values, {
      onError: (error) => {
        if (error instanceof HTTPError && error.response.status === 400 && error.jsonData) {
          const errors = transformValidationErrorToErrorsArray(error);
          errors.forEach((error) => {
            // oxlint-disable-next-line typescript/no-explicit-any
            methods.setError(error.path as any, {
              type: error.code,
              message: error.message,
            });
          });
        }
      },
      onSuccess: (user) => {
        navigate({
          from: "/gym/$venueUuid/staff/add",
          to: "../$userUuid",
          params: { userUuid: user.uuid },
        });
      },
    });
  };

  return (
    <Stack
      sx={{ flex: 1 }}
      component="form"
      autoComplete="off"
      onSubmit={methods.handleSubmit(onSubmit)}
    >
      <Stack spacing={1} sx={{ flex: 1 }}>
        <FormProvider {...methods}>
          <InviteFormFields />
        </FormProvider>
      </Stack>
      <Box
        sx={{
          display: "flex",
          padding: 2,
          backgroundColor: "white",
        }}
      >
        <Button
          type="submit"
          color="primary"
          loading={methods.formState.isSubmitting}
          loadingPosition="start"
          sx={{ flex: 1 }}
          size="lg"
        >
          Send Invite
        </Button>
      </Box>
    </Stack>
  );
};

const VenueRolesSelector = () => {
  const { selectedVenues } = useSelectedVenues();
  const { data: allVenuesData } = useAllVenueStaffData(
    selectedVenues.length ? { venues: selectedVenues } : undefined,
  );
  const venues = allVenuesData?.venues ?? emptyArray;

  const { control } = useFormContext<ApiInviteStaffPayload>();
  const { field, fieldState } = useController({
    name: "roles",
    control,
  });

  const rolesByVenue = useMemo(() => {
    return Object.fromEntries(field.value?.map((role) => [role.venue, role.role]) ?? []);
  }, [field.value]);

  const borderColor = fieldState.error ? "danger.300" : "neutral.200";

  return (
    <FormGroup error={Boolean(fieldState.error)}>
      <FormLabel sx={{ marginBottom: 1 }}>Roles</FormLabel>
      <Stack
        sx={{
          borderRadius: 8,
          border: "1px solid",
          borderColor,
          width: "100%",
          ".venue-role": {
            padding: 1,
            borderBottom: "1px solid",
            borderColor,
            "&:last-child": {
              borderBottom: "none",
            },
          },
        }}
      >
        {venues.map((venue) => {
          return (
            <Stack
              alignItems="center"
              spacing={1}
              direction="row"
              className="venue-role"
              key={venue.uuid}
              justifyContent="space-between"
            >
              <Stack direction="row" spacing={0.5}>
                <MuiAvatar
                  src={venue.logoUrl ? getThumbnailUrl(venue.logoUrl) : undefined}
                  alt={venue.name}
                  sx={{
                    width: 24,
                    height: 24,
                    borderRadius: 4,
                  }}
                />
                <Typography level="body-md">{venue.name}</Typography>
              </Stack>
              <StaffRoleUi
                role={rolesByVenue[venue.uuid] ?? null}
                isActive={!!rolesByVenue[venue.uuid]}
                showDeactivate={!!rolesByVenue[venue.uuid]}
                onChange={(role, isActive) => {
                  const newRoles = field.value?.filter((r) => r.venue !== venue.uuid) ?? [];
                  if (isActive && role) {
                    newRoles.push({ venue: venue.uuid, role });
                  }
                  field.onChange(newRoles);
                }}
              />
            </Stack>
          );
        })}
      </Stack>
      {fieldState.error?.message ? (
        <FormHelperText>{fieldState.error?.message}</FormHelperText>
      ) : null}
    </FormGroup>
  );
};

const InviteFormFields = () => {
  return (
    <>
      <FormSection>
        <TextInput name="email" label="Email" type="email" />
      </FormSection>
      <FormSection>
        <TextInput name="firstName" label="First name" />
      </FormSection>
      <FormSection>
        <TextInput name="lastName" label="Last name" />
      </FormSection>
      <FormSection>
        <VenueRolesSelector />
      </FormSection>
    </>
  );
};
